import { toZonedTime } from "date-fns-tz";
import { useAtomValue } from "jotai";
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { useTranslation } from "react-i18next";
import { NavLink, useLocation, useParams } from "react-router-dom";

import { InvoiceReady } from "~/components/invoice-ready/invoice-ready";
import { SaleInfo } from "~/components/sale-info/sale-info";
import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from "~/components/ui/alert";
import { CheckIcon } from "~/components/ui/icons/check";
import { ClockIcon } from "~/components/ui/icons/clock";
import { ShopifyErrorIcon } from "~/components/ui/icons/shopify-error";
import { useOrdersCountersQuery } from "~/graphql/graphql-new.gen";
import type { BrandMatch } from "~/routes/root";
import { brandState, serverTimeState } from "~/stores/brand";
import {
  activeSaleIdState,
  activeSaleState,
  isCurrent,
  isEnded,
} from "~/stores/sales";
import { getIsInvoiceReady } from "~/utils/billing";
import { formatDate } from "~/utils/date";

import { useBillingState } from "../billing/use-billing-state";

export const HomeOrders: React.FC = () => {
  const { search } = useLocation();
  const { brandId = "" } = useParams<BrandMatch>();
  const brand = useAtomValue(brandState);
  const { i18n, t } = useTranslation();

  const activeSaleId = useAtomValue(activeSaleIdState);
  const activeSale = useAtomValue(activeSaleState);
  const serverTime = useAtomValue(serverTimeState);

  const { data: billingState } = useBillingState();

  const { data: ordersCounterData } = useOrdersCountersQuery(
    {
      sellerId: brandId,
      saleId: activeSaleId ?? "",
    },
    {
      enabled: !!activeSaleId,
      refetchOnWindowFocus: false,
    },
  );

  if (!activeSale) return null;

  if (
    (ordersCounterData?.ordersCounters.tagging.brand.actionRequired ?? 0) > 0
  ) {
    return (
      <section className="flex flex-col space-y-4">
        {isEnded(activeSale, serverTime) ? (
          <Alert className="w-full rounded-lg border-[#C6E7D5] bg-[#ECF7F1] [&>svg]:text-green-900">
            <CheckIcon />
            <AlertTitle>{t("home.closed_sale_title")}</AlertTitle>
            <AlertDescription>{t("home.closed_sale_p")}</AlertDescription>
          </Alert>
        ) : null}
        {brand?.plugins?.shopify && !brand.plugins.shopify.token ? (
          <Alert
            appearance="error"
            className="rounded-lg has-[>div[data-slot=alert-action]]:pr-30"
          >
            <ShopifyErrorIcon />
            <AlertTitle>{t("home.shopify_error_title")}</AlertTitle>
            <AlertDescription>{t("home.shopify_error_text")}</AlertDescription>
            <AlertAction>
              <NavLink
                to={`/${brandId}/settings/integrations${search}`}
                type="button"
                className="rounded-lg border-2 border-red-350 bg-white px-3.5 py-2 text-sm font-bold text-gray-900 outline-none transition-colors duration-300 hover:border-red-600 hover:text-gray-700 focus-visible:ring-2 focus-visible:ring-red-600/30"
              >
                {t("sidebar.settings")}
              </NavLink>
            </AlertAction>
          </Alert>
        ) : null}
        <div className="flex w-full justify-between rounded-lg border border-gray-300 p-6">
          <div className="flex items-center space-x-3">
            <ClockIcon className="size-6 text-gray-500" />
            <span className="font-semibold text-gray-900">
              {t("home.alerts.orders.order_required_actions", {
                count:
                  ordersCounterData?.ordersCounters.tagging.brand
                    .actionRequired,
              })}
            </span>
          </div>
          <NavLink
            to={`/${brand?.id ?? ""}/orders?view=required-action`}
            className="px-2 py-1 text-sm font-semibold text-green-900"
          >
            {t("home.alerts.orders.show_orders")}
          </NavLink>
        </div>
      </section>
    );
  }

  if (
    isCurrent(activeSale, serverTime) &&
    (ordersCounterData?.ordersCounters.tagging.brand.actionRequired ?? 0) ===
      0 &&
    (ordersCounterData?.ordersCounters.tagging.brand.available ?? 0) > 0
  ) {
    return (
      <section>
        <div className="flex w-full justify-between rounded-lg border border-gray-300 p-6">
          <div className="flex items-center space-x-3">
            <CheckIcon className="size-6 text-gray-500" />
            <span className="font-semibold text-gray-900">
              {t("home.alerts.orders.all_caught_up")}
            </span>
          </div>
          <NavLink
            to={`/${brand?.id ?? ""}/orders?view=available`}
            className="px-2 py-1 text-sm font-semibold text-green-900"
          >
            {t("home.alerts.orders.see_all_orders")}
          </NavLink>
        </div>
      </section>
    );
  }

  if (
    !isCurrent(activeSale, serverTime) &&
    (ordersCounterData?.ordersCounters.tagging.brand.available ?? 0) > 0
  ) {
    if (getIsInvoiceReady(billingState)) {
      return (
        <section>
          <InvoiceReady />
        </section>
      );
    }
    return (
      <section>
        <SaleInfo
          variant="invoice-waiting"
          title={t("orders.ended_wait")}
          subtitle={t("invoice.wait_text", {
            withdrawalPeriod: billingState?.withdrawalPeriodInDays,
          })}
        >
          {billingState?.withdrawalPeriodEndDate ? (
            <p className="rounded-full bg-green-100 px-4 py-1.5 text-sm font-bold text-green-900">
              {formatDate(
                brand?.timezone
                  ? toZonedTime(
                      new Date(billingState.withdrawalPeriodEndDate),
                      brand.timezone,
                    )
                  : new Date(billingState.withdrawalPeriodEndDate),
                i18n.language.replace("_", "-"),
                {
                  dateStyle: "long",
                  timeStyle: "medium",
                },
              )}
            </p>
          ) : null}
        </SaleInfo>
      </section>
    );
  }
  return (
    <section>
      <SaleInfo
        variant="sale-ready"
        title={t("home.no_orders")}
        subtitle={t("home.no_orders_p")}
      />
    </section>
  );
};
